'use client'

import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { InstantSearchNext } from 'react-instantsearch-nextjs'

import { useRef } from 'react'

import ListArchiveItems from '@/components/list-archive/ListArchiveItems'

import FilterToolbar from '@/components/filters/FilterToolbar'
import FilterModal from '@/components/filters/FilterModal'
import FilterModalFooter from '@/components/filters/FilterModalFooter'
import FilterModalComponents from '@/components/filters/FilterModalComponents'
import FilterStats from '@/components/filters/FilterStats'
import FilterSearchBox from '@/components/filters/FilterSearchBox'
import FilterPagination from '@/components/filters/FilterPagination'

import type { FilterPropsType, InitialFilterPropsType } from '@/layouts/types'

import { Configure } from 'react-instantsearch'

type ListArchiveWrapperProps = {
  archivePost: {
    acf: unknown
  }
  algoliaIndex: string
  filters: FilterPropsType
  initialFilters?: InitialFilterPropsType
}

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY,
)

export default function ListArchiveWrapper({
  archivePost,
  algoliaIndex,
  filters,
}: ListArchiveWrapperProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null)
  const termName = archivePost?.name ?? ''
  const parentTermName = archivePost?.term_parent_name ?? ''

  // Handle various default / initial states
  let config = ''

  if (termName && !parentTermName) {
    config = `taxonomies_hierarchical.all-business-categories.lvl0:"${termName}"`
  }

  if (termName && parentTermName) {
    config = `taxonomies_hierarchical.all-business-categories.lvl1:"${parentTermName} > ${termName}"`
  }

  const yesterdayUnix = Math.floor(Date.now() / 1000) - 86400

  if (algoliaIndex === 'wp_posts_all-events') {
    if (termName) {
      config += `AND hide_date_unix>${yesterdayUnix}`
    } else {
      config += `hide_date_unix>${yesterdayUnix}`
    }
  }

  return (
    <InstantSearchNext
      searchClient={searchClient}
      indexName={algoliaIndex}
      routing
      future={{
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: true,
      }}>
      <Configure hitsPerPage={999} filters={config} />

      <div className="hidden" aria-hidden="true">
        <FilterModalComponents filters={filters} />
      </div>

      <div className="container ms:mt-m">
        <FilterToolbar
          leftChildren={
            !termName ? (
              <div className="flex items-center gap-x-s">
                <FilterModal footerComponent={<FilterModalFooter />}>
                  <FilterModalComponents filters={filters} />
                </FilterModal>
              </div>
            ) : null
          }
          rightChildren={
            <div className="flex w-full justify-end gap-x-l">
              <FilterSearchBox />
            </div>
          }
        />
      </div>

      <div
        ref={gridRef}
        className="container grid grid-cols-1 gap-y-xl"
        style={{ scrollMarginTop: '80px' }}>
        <ListArchiveItems />
      </div>

      <div className="my-m border-t pt-m text-center">
        <div className="container flex items-center justify-between ms:flex-col ms:gap-y-s">
          <FilterStats />
          <FilterPagination gridRef={gridRef} />
        </div>
      </div>
    </InstantSearchNext>
  )
}
